import React from "react";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { REWARD_TYPES } from "../../../constants/promotionConstants";

const REWARD_TARGET_OPTIONS = [
  { value: "product", label: "Specific Product(s)" },
  { value: "category", label: "Category (any product in category)" },
  { value: "brand", label: "Brand (any product from brand)" },
  { value: "combo", label: "Combo (specific product set)" },
];

const StepReward = ({ promotion, onChange, menuItems, categories = [] }) => {
  const reward = promotion.reward;
  const targetType = reward.targetType || "product";

  const getCategoryVariants = (categoryId) => {
    if (!categoryId) return [];
    const catIdNorm = String(categoryId).toLowerCase().trim();
    const products = menuItems.filter(p => {
      const pCatId = String(p.categoryId || "").toLowerCase().trim();
      return pCatId === catIdNorm;
    });
    const seen = new Set();
    const result = [];
    products.forEach(p => {
      const variants = p.variantValueIds || [];
      variants.forEach(v => {
        const vNorm = String(v).toLowerCase().trim();
        if (vNorm && !seen.has(vNorm)) {
          seen.add(vNorm);
          result.push(v);
        }
      });
    });
    return result;
  };

  // Synchronize reward.entities whenever target properties or raw fields change
  React.useEffect(() => {
    if (reward.type !== "freeProduct") {
      if (reward.entities?.length) onChange("entities", []);
      return;
    }

    let entities = [];
    const rewardQty = Number(reward.quantity) || 1;
    if (targetType === "product") {
      entities = (reward.productIds || []).map(p => ({
        type: "PRODUCT",
        entityId: p.productId,
        variantId: p.variantId || "any",
        quantity: rewardQty
      }));
    } else if (targetType === "category") {
      if (reward.rewardCategory) {
        entities = [{
          type: "CATEGORY",
          entityId: reward.rewardCategory,
          variantId: reward.rewardVariantId || "any",
          quantity: rewardQty
        }];
      }
    } else if (targetType === "combo") {
      entities = (reward.rewardCombo || []).map(p => ({
        type: "PRODUCT",
        entityId: p.productId,
        variantId: p.variantId || "any",
        quantity: Number(p.quantity) || 1
      }));
    }

    const currentSerialized = JSON.stringify(reward.entities || []);
    const newSerialized = JSON.stringify(entities);
    if (currentSerialized !== newSerialized) {
      onChange("entities", entities);
    }
  }, [reward.type, targetType, reward.productIds, reward.rewardCategory, reward.rewardVariantId, reward.rewardCombo, reward.quantity]);

  // ── Free-product sub-handlers ──────────────────────────────────────────────
  const handleFreeProductChange = (index, field, value) => {
    const updated = [...(reward.productIds || [])];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "productId") updated[index].variantId = "any";
    onChange("productIds", updated);
  };

  const addFreeProduct = () =>
    onChange("productIds", [...(reward.productIds || []), { productId: "", variantId: "any" }]);

  const removeFreeProduct = (index) => {
    const updated = [...(reward.productIds || [])];
    updated.splice(index, 1);
    onChange("productIds", updated);
  };

  const handleComboRewardChange = (index, field, value) => {
    const updated = [...(reward.rewardCombo || [])];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "productId") updated[index].variantId = "any";
    onChange("rewardCombo", updated);
  };

  const addComboRewardItem = () =>
    onChange("rewardCombo", [...(reward.rewardCombo || []), { productId: "", variantId: "any" }]);

  const removeComboRewardItem = (index) => {
    const updated = [...(reward.rewardCombo || [])];
    updated.splice(index, 1);
    onChange("rewardCombo", updated);
  };

  return (
    <div>
      <h5 className="mb-4">Reward Rules</h5>

      {/* ── Reward Type ───────────────────────────────────────────────────── */}
      <Row className="mb-4">
        <Col md={6}>
          <FormGroup>
            <Label for="rewardType">Reward Type</Label>
            <Input
              type="select"
              id="rewardType"
              value={reward.type}
              onChange={(e) => onChange("type", e.target.value)}
            >
              {REWARD_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>

      {/* ── Free Product section ──────────────────────────────────────────── */}
      {reward.type === "freeProduct" && (
        <div className="mb-4 border rounded p-3 bg-light">

          {/* Reward Target selector */}
          <Row className="mb-3">
            <Col md={12}>
              <Label className="fw-bold d-block mb-2">Reward Target</Label>
              <div className="d-flex flex-wrap gap-3">
                {REWARD_TARGET_OPTIONS.map((opt) => (
                  <div key={opt.value} className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`target-${opt.value}`}
                      name="rewardTarget"
                      value={opt.value}
                      checked={targetType === opt.value}
                      onChange={() => onChange("targetType", opt.value)}
                    />
                    <label className="form-check-label" htmlFor={`target-${opt.value}`}>
                      {opt.label}
                    </label>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          {/* ── PRODUCT: specific product + variant picker ─────────────────── */}
          {targetType === "product" && (
            <>
              <div className="mb-3">
                <Label className="fw-bold d-block mb-2">Reward Products &amp; Variants</Label>
                <Button color="secondary" outline size="sm" onClick={addFreeProduct}>
                  <i className="mdi mdi-plus me-1" /> Add Product / Variant
                </Button>
              </div>
              {(reward.productIds || []).map((itemConfig, index) => {
                const selectedProduct = menuItems.find((item) => item.productId === itemConfig.productId);
                const availableVariants = selectedProduct?.variantEnabled
                  ? ["any", ...(selectedProduct.variantValueIds || [])]
                  : ["any", "standard"];
                return (
                  <Row key={index} className="align-items-end mb-3 g-2">
                    <Col md={5}>
                      <FormGroup className="mb-0">
                        <Label className="small text-muted mb-1 font-weight-bold">Product</Label>
                        <Input
                          type="select"
                          value={itemConfig.productId || ""}
                          onChange={(e) => handleFreeProductChange(index, "productId", e.target.value)}
                        >
                          <option value="">Select product</option>
                          {menuItems.map((item) => (
                            <option key={item.productId || item.id} value={item.productId}>
                              {item.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className="mb-0">
                        <Label className="small text-muted mb-1 font-weight-bold">Variant</Label>
                        <Input
                          type="select"
                          value={itemConfig.variantId || "any"}
                          onChange={(e) => handleFreeProductChange(index, "variantId", e.target.value)}
                        >
                          {availableVariants.map((v) => (
                            <option key={v} value={v}>
                              {v === "any" ? "Any Variant" : v === "standard" ? "Standard" : v}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <Button color="danger" outline size="sm" onClick={() => removeFreeProduct(index)}>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                );
              })}
            </>
          )}

          {/* ── CATEGORY: category dropdown + variant picker ───────────────── */}
          {targetType === "category" && (() => {
            const categoryVariants = getCategoryVariants(reward.rewardCategory);
            return (
              <Row className="mb-3">
                <Col md={6}>
                  <FormGroup>
                    <Label for="rewardCategory">Reward Category</Label>
                    <Input
                      type="select"
                      id="rewardCategory"
                      value={reward.rewardCategory || ""}
                      onChange={(e) => {
                        onChange("rewardCategory", e.target.value);
                        onChange("rewardVariantId", "any");
                      }}
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id || cat.categoryId} value={cat.id || cat.categoryId}>
                          {cat.category}
                        </option>
                      ))}
                    </Input>
                    <small className="text-muted">
                      Customer gets a free product from this category (policy decides which one).
                    </small>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="rewardVariantId">Reward Variant Constraint</Label>
                    <Input
                      type="select"
                      id="rewardVariantId"
                      value={reward.rewardVariantId || "any"}
                      onChange={(e) => onChange("rewardVariantId", e.target.value)}
                    >
                      <option value="any">Any Variant</option>
                      {categoryVariants.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Input>
                    <small className="text-muted">
                      Constraint for the reward product variant.
                    </small>
                  </FormGroup>
                </Col>
              </Row>
            );
          })()}

          {/* ── BRAND: brand text input ───────────────────────────────────── */}
          {targetType === "brand" && (
            <Row className="mb-3">
              <Col md={6}>
                <FormGroup>
                  <Label for="rewardBrand">Reward Brand</Label>
                  <Input
                    id="rewardBrand"
                    value={reward.rewardBrand || ""}
                    onChange={(e) => onChange("rewardBrand", e.target.value)}
                    placeholder="e.g. Domino's"
                  />
                  <small className="text-muted">
                    Customer gets a free product from this brand (policy decides which one).
                  </small>
                </FormGroup>
              </Col>
            </Row>
          )}

          {/* ── COMBO: multi-product combo builder ───────────────────────── */}
          {targetType === "combo" && (
            <>
              <div className="mb-3">
                <Label className="fw-bold d-block mb-2">Reward Combo Products</Label>
                <Button color="secondary" outline size="sm" onClick={addComboRewardItem}>
                  <i className="mdi mdi-plus me-1" /> Add Combo Product
                </Button>
              </div>
              {(reward.rewardCombo || []).map((itemConfig, index) => {
                const selectedProduct = menuItems.find((item) => item.productId === itemConfig.productId);
                const availableVariants = selectedProduct?.variantEnabled
                  ? ["any", ...(selectedProduct.variantValueIds || [])]
                  : ["any", "standard"];
                return (
                  <Row key={index} className="align-items-end mb-3 g-2">
                    <Col md={5}>
                      <FormGroup className="mb-0">
                        <Label className="small text-muted mb-1 font-weight-bold">Product</Label>
                        <Input
                          type="select"
                          value={itemConfig.productId || ""}
                          onChange={(e) => handleComboRewardChange(index, "productId", e.target.value)}
                        >
                          <option value="">Select product</option>
                          {menuItems.map((item) => (
                            <option key={item.productId || item.id} value={item.productId}>
                              {item.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className="mb-0">
                        <Label className="small text-muted mb-1 font-weight-bold">Variant</Label>
                        <Input
                          type="select"
                          value={itemConfig.variantId || "any"}
                          onChange={(e) => handleComboRewardChange(index, "variantId", e.target.value)}
                        >
                          {availableVariants.map((v) => (
                            <option key={v} value={v}>
                              {v === "any" ? "Any Variant" : v === "standard" ? "Standard" : v}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <Button color="danger" outline size="sm" onClick={() => removeComboRewardItem(index)}>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                );
              })}
            </>
          )}

          {/* ── Reward Quantity (all target types) ───────────────────────── */}
          <Row className="g-3 mt-1">
            <Col md={4}>
              <FormGroup className="mb-0">
                <Label for="rewardQuantity">Reward Quantity</Label>
                <Input
                  id="rewardQuantity"
                  type="number"
                  min="1"
                  value={reward.quantity}
                  onChange={(e) => onChange("quantity", e.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
      )}

      {/* ── Percentage Discount ───────────────────────────────────────────── */}
      {reward.type === "percentageDiscount" && (
        <Row className="mb-3">
          <Col md={4}>
            <FormGroup>
              <Label for="percent">Discount %</Label>
              <Input
                id="percent"
                type="number"
                min="0"
                max="100"
                value={reward.percent}
                onChange={(e) => onChange("percent", e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for="maximumDiscount">Maximum Discount</Label>
              <Input
                id="maximumDiscount"
                type="number"
                min="0"
                value={reward.maximumDiscount}
                onChange={(e) => onChange("maximumDiscount", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      )}

      {/* ── Fixed Price ───────────────────────────────────────────────────── */}
      {reward.type === "fixedPrice" && (
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Label for="targetPrice">Target Price (₹)</Label>
              <Input
                id="targetPrice"
                type="number"
                min="0"
                value={reward.targetPrice}
                onChange={(e) => onChange("targetPrice", e.target.value)}
              />
              <small className="text-muted">
                Every qualifying item (from the Qualification step — e.g. "any
                Regular Pizza") is priced down to this amount. Set the
                qualifying item's variant on the Qualification step, not here —
                this reward applies directly to what the customer already buys,
                it doesn't give away a separate item.
              </small>
            </FormGroup>
          </Col>
        </Row>
      )}

      {/* ── Flat / Combo Price ────────────────────────────────────────────── */}
      {(reward.type === "flatDiscount" || reward.type === "comboPrice") && (
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Label for="amount">Discount Amount</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                value={reward.amount}
                onChange={(e) => onChange("amount", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      )}

      {/* ── Reward Points ─────────────────────────────────────────────────── */}
      {reward.type === "rewardPoints" && (
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Label for="points">Reward Points</Label>
              <Input
                id="points"
                type="number"
                min="0"
                value={reward.points}
                onChange={(e) => onChange("points", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      )}

      {/* ── Wallet Credit ─────────────────────────────────────────────────── */}
      {reward.type === "walletCredit" && (
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup>
              <Label for="walletCredit">Wallet Credit</Label>
              <Input
                id="walletCredit"
                type="number"
                min="0"
                value={reward.walletCredit}
                onChange={(e) => onChange("walletCredit", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default StepReward;
